import { Button, DialogActions, DialogTitle, MenuItem, Select } from "@material-ui/core";
import React, { useEffect, useState } from "react";

import { IPresentationMetadata, IWorkspacePermission, WorkspaceResourceType, WorkspaceUserRole } from "common/interfaces";
import { getStaticUrl } from "js/config";
import WorkspaceController, { getWorkspacePermissionsToResource } from "js/controllers/WorkspaceController";
import AppController from "js/core/AppController";
import getLogger, { LogGroup } from "js/core/logger";
import { BeautifulDialog, DialogContent, ShowErrorDialog } from "js/react/components/Dialogs/BaseDialog";
import FetchingClickShield from "js/react/components/FetchingClickShield";
import { FlexBox } from "js/react/components/LayoutGrid";
import { FormIcon } from "js/react/components/Standalone";
import withProgressDialog from "js/core/utilities/withProgressDialog";
import { presentations as presentationsApi } from "apis/callables";

const logger = getLogger(LogGroup.TEAMS);

const baiBotImage = getStaticUrl("/images/bai-bot/bai-bot-shake.gif");

interface MovePresentationToWorkspaceDialogProps {
    presentations: IPresentationMetadata[];
    closeDialog: () => void;
}

function MovePresentationToWorkspaceDialog({ presentations, closeDialog }: MovePresentationToWorkspaceDialogProps) {
    const workspaces = AppController.workspacesMetadata.filter(w => w.userRole !== WorkspaceUserRole.GUEST);

    const [fetching, setFetching] = useState(true);
    const [workspaceId, setWorkspaceId] = useState<string | null>(workspaces.find(w => w.id !== AppController.workspaceId)?.id);
    const [presentationPermissions, setPresentationPermissions] = useState<{ [presentationId: string]: IWorkspacePermission[] }>({});

    useEffect(() => {
        (async () => {
            try {
                setFetching(true);
                const permissions: { [presentationId: string]: IWorkspacePermission[] } = {};
                await Promise.all(presentations.map(async presentation => {
                    const workspacePermissions = await getWorkspacePermissionsToResource({ workspaceId: AppController.workspaceId, resourceId: presentation.id, resourceType: WorkspaceResourceType.PRESENTATION });
                    permissions[presentation.id] = workspacePermissions;
                }));
                setPresentationPermissions(permissions);
                setFetching(false);
            } catch (err) {
                logger.error(err, "[MovePresentationToWorkspaceDialog] Failed to fetch presentation permissions");
            }
        })();
    }, presentations.map(p => p.id));

    const presentationsToMove = presentations.filter(p => p.permissions.owner);
    const presentationsToKeep = presentations.filter(p => !p.permissions.owner);

    const sharedPresentationsToMove = presentationsToMove.filter(p => presentationPermissions[p.id]?.length > 1);

    const handleMovePresentations = () => {
        withProgressDialog({
            title: "Moving presentations...",
            action: async reportProgress => {
                try {
                    for (const presentation of presentationsToMove) {
                        await presentationsApi.moveWorkspace({ id: presentation.id, newWorkspaceId: workspaceId });
                        await WorkspaceController.forceRefreshWorkspaceResource(presentation.id, WorkspaceResourceType.PRESENTATION);
                        if (presentationsToMove.length > 1) {
                            reportProgress((1 + presentationsToMove.indexOf(presentation)) / presentationsToMove.length);
                        }
                    }

                    closeDialog();
                } catch (err) {
                    logger.error(err, "[MovePresentationToWorkspaceDialog] Failed to move presentations");

                    ShowErrorDialog({
                        title: "Unable to move presentations",
                        message: err.message
                    });
                }
            }
        });
    };

    return (
        <BeautifulDialog closeDialog={closeDialog}>
            <FetchingClickShield visible={fetching} backgroundColor={"white"} />
            <DialogTitle>
                <FormIcon src={baiBotImage} />
                <FlexBox middle fillWidth style={{ textAlign: "center" }}>
                    <span>
                        {
                            !fetching && sharedPresentationsToMove.length > 0
                                ? "Are you sure you want to move these presentations?"
                                : "Move presentations"
                        }
                    </span>
                </FlexBox>
            </DialogTitle>
            <DialogContent>
                {
                    sharedPresentationsToMove.length > 0 &&
                    <>
                        <div style={{ textAlign: "center" }}>
                            Any presentations currently shared in a Team Folder will be removed from<br />those folders and the collaborators will no longer have access.
                        </div>
                        <br />
                    </>
                }
                <div style={{ display: "flex", width: "100%" }}>
                    <h2 style={{ margin: "6px 0 0 0" }}>Move to:</h2>
                    <Select
                        style={{ marginLeft: "20px", flex: 1 }}
                        value={workspaceId}
                        onChange={event => setWorkspaceId(event.target.value as string)}
                    >
                        {workspaces
                            .filter(workspace => workspace.id !== AppController.workspaceId)
                            .sort((a, b) => a.createdAt - b.createdAt)
                            .map(ws => <MenuItem key={ws.id} value={ws.id}>{ws.name}</MenuItem>)}
                    </Select>
                </div>
                {presentationsToKeep.length > 0 &&
                    <div>
                        <p>The following presentations could not be moved because they were shared with you.
                            You can only move presentations that you own.</p>
                        <p><b><i>{presentationsToKeep.map(p => p.name).join(", ")}</i></b></p>
                    </div>
                }
            </DialogContent>
            <DialogActions>
                <Button onClick={closeDialog}>Cancel</Button>
                <Button onClick={handleMovePresentations} color="primary" disabled={presentationsToMove.length === 0}>Move Presentations</Button>
            </DialogActions>
        </BeautifulDialog>
    );
}

export default MovePresentationToWorkspaceDialog;
